import { useEffect, useState } from "react";
import { UserAgentData, useUserAgentData } from "./use-user-agent-data";
import { ChipStatus } from "../components/shared/ppr-chip-status/ppr-chip-status";
import { extractMajorVer } from "../components/util/stringUtils";
import { sleep } from "../components/util/util";

export type OSInfo = {
  name?: string;
  version: number;
  fullInfo: string;
};

export type SupportedOS = {
  name: string;
  minVersion: number;
  maxVersion: number;
};

export type OSInputResult = {
  status: boolean;
  osInfo: string;
};

export const useOSCheck = (
  shouldRun: boolean,
  listSupportedOS: SupportedOS[],
  inputResult?: OSInputResult,
) => {
  const { userAgentData } = useUserAgentData();
  const [status, setStatus] = useState<ChipStatus>("not-started");
  const [osInfo, setOSInfo] = useState<OSInfo>();

  useEffect(() => {
    const validateOS = (osInfo: OSInfo) => {
      if (inputResult) return inputResult.status;

      return listSupportedOS.some((os) => {
        return (
          os.name === osInfo.name &&
          (osInfo.version === 0 ||
            (os.minVersion <= osInfo.version &&
              osInfo.version <= os.maxVersion))
        );
      });
    };

    const runOSCheck = async (userAgentData: UserAgentData) => {
      setStatus("analyzing");
      await sleep();
      const osInfo: OSInfo = {
        name: userAgentData.platform,
        version:
          userAgentData.platformVersion !== ""
            ? parseInt(
                extractMajorVer(userAgentData.platformVersion ?? "") as string,
              )
            : 0,
        fullInfo: "",
      };

      switch (osInfo.name) {
        case "Windows":
          if (osInfo.version >= 13) {
            osInfo.version = 11;
          } else if (osInfo.version > 0) {
            osInfo.version = 10;
          }
          break;
        case "macOS":
          osInfo.name = "MacOS";
          break;
      }

      const validationRes = validateOS(osInfo);
      osInfo.fullInfo = inputResult
        ? inputResult.osInfo
        : `${osInfo.name} ${osInfo.version !== 0 ? osInfo.version : ""}`;

      setStatus(validationRes ? "passed" : "failed");
      setOSInfo({ ...osInfo });
    };

    if (shouldRun && userAgentData) runOSCheck(userAgentData);
  }, [shouldRun, userAgentData]);

  return {
    osCheckStatus: status,
    osInfo: osInfo,
  };
};
